import { useEffect, useRef } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useSelectedGcsConfigsStore } from '../../store/useSelectedGcsConfigsStore';
import { useFocusMap } from '../../hooks/useFocusMap';
import { AsyncStatus } from '../../types/general/asyncAction';
import type { GcsStore } from '../../store/useGcsStore';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';

interface InitialMapFocusListenerProps {
  mapRef: RefObject<MapRef | null>;
}

export function InitialMapFocusListener({ mapRef }: InitialMapFocusListenerProps) {
  const focusMap = useFocusMap(mapRef);
  const hasFocusedRef = useRef(false);
  const selectedConfigs = useSelectedGcsConfigsStore((state) => state.selectedConfigs);

  useEffect(() => {
    const selectFlightNetworkStates = (state: GcsStore): Map<string, AsyncStatus> => {
      const states = new Map<string, AsyncStatus>();
      selectedConfigs.forEach((config) => {
        const gcsData = state.data.get(config.id);
        const flightNetworkStatus = gcsData?.flightNetwork?.status ?? AsyncStatus.INITIAL;
        states.set(config.id, flightNetworkStatus);
      });
      return states;
    };

    // Custom equality function to compare Map contents
    const equalityFn = (a: Map<string, AsyncStatus>, b: Map<string, AsyncStatus>): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectFlightNetworkStates,
      (nextStates) => {
        // Skip if already focused or no configs selected
        if (hasFocusedRef.current || selectedConfigs.length === 0) {
          return;
        }

        // Check if all control zones are loaded (success or failure)
        const allLoaded = Array.from(nextStates.values()).every(
          (status) => status === AsyncStatus.SUCCESS || status === AsyncStatus.FAILURE
        );

        if (allLoaded) {
          hasFocusedRef.current = true;
          focusMap();
        }
      },
      { equalityFn }
    );

    return unsubscribe;
  }, [focusMap, selectedConfigs]);

  // Reset the focus flag when selected configs change
  useEffect(() => {
    hasFocusedRef.current = false;
  }, [selectedConfigs]);

  return null;
}
